import type { FormEvent } from 'react';
import React from 'react';

import { FieldSet, FormField, Input, Label, LabeledControl, Legend, Radio } from '@zillow/constellation';

import type { Section } from '../../../models';
import { NOTICES_OF_CODE_VIOLATIONS } from '../../../constants';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function NoticesOfCodeViolations({ section, setSectionValues }: Props) {
  function onNoNotices(): void {
    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IS_ENABLED]: false,
    });
  }

  function onYesNotices(): void {
    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onCaseNumberChange(event: FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER]: value,
    });
  }

  function onIdentificationNumberChange(event: FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER]: value,
    });
  }

  function onListingChange(event: FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.LISTING_OF_CODE_VIOLATION]: value,
    });
  }

  function onCaseNumberSelect(_id: string): void {
    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER_IS_ENABLED]: false,
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER_IS_ENABLED]: true,
    });
  }

  function onIdentificationNumberSelect(_id: string): void {
    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER_IS_ENABLED]: true,
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER_IS_ENABLED]: false,
    });
  }

  return (
    <FormField
      label={<Label size="lg">In the past 12 months, have you received any notices of code violations?</Label>}
      control={
        <YesAndNoExpansion
          yesHandler={onYesNotices}
          noHandler={onNoNotices}
          yesActive={section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IS_ENABLED)}
        >
          <FormField
            label={<Label>Listing of code violation</Label>}
            control={
              <Input
                value={section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.LISTING_OF_CODE_VIOLATION)}
                onChange={onListingChange}
              />
            }
          />
          <FieldSet marginTop="xs" legend={<Legend>What notice number do you have?</Legend>}>
            <LabeledControl
              controlId="case-number"
              label={<Label>Case number</Label>}
              control={
                <Radio
                  name="number-option"
                  checked={section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER_IS_ENABLED)}
                  onChange={onCaseNumberSelect}
                />
              }
            />
            <LabeledControl
              controlId="identification-number"
              label={<Label>Identification number</Label>}
              control={
                <Radio
                  name="number-option"
                  checked={section.getFieldValue(
                    NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER_IS_ENABLED,
                  )}
                  onChange={onIdentificationNumberSelect}
                />
              }
            />
          </FieldSet>
          {section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER_IS_ENABLED) && (
            <FormField
              marginTop="xs"
              label={<Label>Case number</Label>}
              control={
                <Input
                  value={section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER)}
                  onChange={onCaseNumberChange}
                  marginBottom="xs"
                />
              }
            />
          )}
          {section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER_IS_ENABLED) && (
            <FormField
              marginTop="xs"
              label={<Label>Identification number</Label>}
              control={
                <Input
                  value={section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER)}
                  onChange={onIdentificationNumberChange}
                  marginBottom="xs"
                />
              }
            />
          )}
        </YesAndNoExpansion>
      }
    />
  );
}

NoticesOfCodeViolations.description = 'Chicago specific screen';

export default NoticesOfCodeViolations;
